import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Button, Chip, Snackbar, Alert, LinearProgress,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { AuthContext, MyContext } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { PostData } from '../model/types';
import { discountedWalletCost } from '../lib/powerHour';
import HourglassFullIcon from '../resource/HourglassFullIcon';

/**
 * LAST CALL — a daily ritual that appears only between 23:00 and 01:00 UTC.
 * During this 2-hour window, we surface the posts about to die in the next
 * 2 hours and give the logged-in user a one-click "save" button that upvotes
 * them with the user's default vote time. It's a communal salvation moment,
 * intentionally distinct from the always-on DeathRow (<1h) widget.
 *
 * Outside the window: component returns null.
 */

function isInLastCallWindow(now: Date = new Date()): boolean {
  const h = now.getUTCHours();
  return h === 23 || h === 0;
}

function minutesUntilMidnight(now: Date = new Date()): number {
  const midnight = new Date(now);
  midnight.setUTCHours(24, 0, 0, 0);
  return Math.max(0, Math.round((midnight.getTime() - now.getTime()) / 60000));
}

function LastCall() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const allPosts = useContext<CacheContextType>(CacheContext).allPosts;
  const getUserRecord = useContext<CacheContextType>(CacheContext).getUserRecord;

  const [visible, setVisible] = useState(() => isInLastCallWindow());
  const [minutesLeft, setMinutesLeft] = useState(() => minutesUntilMidnight());
  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Tick visibility + midnight countdown every 30s
  useEffect(() => {
    const tick = () => {
      setVisible(isInLastCallWindow());
      setMinutesLeft(minutesUntilMidnight());
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  // Posts expiring in the next 2 hours, soonest first
  const doomedPosts = useMemo((): PostData[] => {
    const now = DateTime.now();
    const cutoff = now.plus({ hours: 2 });
    return allPosts()
      .filter((p) => {
        if (p.is_expired || p.is_permanent) return false;
        const exp = DateTime.fromISO(p.expire_at);
        return exp > now && exp <= cutoff;
      })
      .sort((a, b) => DateTime.fromISO(a.expire_at).toSeconds() - DateTime.fromISO(b.expire_at).toSeconds())
      .slice(0, 5);
  }, [allPosts]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible || doomedPosts.length === 0) return null;

  const handleSave = async (post: PostData) => {
    if (!userRecord) {
      setToast('Sign in to save posts');
      return;
    }
    setSaving(post.id);
    try {
      const s = userRecord.user_settings;
      const voteSeconds = (s?.voteDays ?? 0) * 86400 + (s?.voteHours ?? 1) * 3600 + (s?.voteMinutes ?? 0) * 60;
      const walletCost = discountedWalletCost(voteSeconds);
      if (walletCost > userRecord.time_wallet_seconds) {
        setToast('Not enough time in your wallet');
        setSaving(null);
        return;
      }

      const postExpiry = DateTime.fromISO(post.expire_at).toSeconds();
      const newExpiry = DateTime.fromSeconds(postExpiry + voteSeconds).toISO();

      // Insert vote row + extend post expiry + deduct wallet
      const [voteRes, postRes, walletRes] = await Promise.all([
        supabase.from('votes').insert({
          user_record_id: userRecord.id,
          object_type: 'posts',
          parent_id: post.id,
          time_value: voteSeconds,
          revived: false,
          killed: false,
          made_permanent: false,
          is_test_mode: false,
        }),
        supabase.from('posts').update({ expire_at: newExpiry, is_expired: false }).eq('id', post.id),
        supabase.from('users').update({
          time_wallet_seconds: userRecord.time_wallet_seconds - walletCost,
        }).eq('id', userRecord.id),
      ]);
      if (voteRes.error || postRes.error || walletRes.error) throw new Error('save failed');
      setToast(`🆘 Saved! +${Math.floor(voteSeconds / 3600) || Math.floor(voteSeconds / 60) + 'm'}h to life.`);
    } catch (e) {
      console.error('LastCall save failed:', e);
      setToast('Save failed — try again');
    } finally {
      setSaving(null);
    }
  };

  const headerText = minutesLeft > 0 && new Date().getUTCHours() === 23
    ? `LAST CALL — ${minutesLeft}m until the daily reset`
    : 'LAST CALL — the witching hour';

  return (
    <>
      <Box
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          border: '2px solid',
          borderColor: '#1a1a2e',
          background: (t) => t.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0a0a18 0%, #1a0a2a 50%, #0a0a18 100%)'
            : 'linear-gradient(135deg, #14143a 0%, #2a1a3a 50%, #14143a 100%)',
          color: 'rgba(255,255,255,0.92)',
          position: 'relative',
          boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2, py: 1.25,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.2)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ fontSize: '1.3rem' }}>🌙</Box>
            <Typography
              sx={{
                fontFamily: '"VT323", monospace',
                fontSize: '1.3rem',
                letterSpacing: 2,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.95)',
              }}
            >
              {headerText}
            </Typography>
          </Stack>
          <Chip
            label={`${doomedPosts.length} DYING`}
            size="small"
            sx={{
              fontFamily: '"VT323", monospace',
              letterSpacing: 1,
              fontSize: '0.8rem',
              height: 22,
              bgcolor: 'rgba(181,30,27,0.8)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          />
        </Box>

        {/* Rows */}
        <Stack divider={<Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.08)' }} />}>
          {doomedPosts.map((post) => {
            const author = getUserRecord(post.user_record_id);
            const minLeft = Math.max(0, Math.round(
              (DateTime.fromISO(post.expire_at).toSeconds() - DateTime.now().toSeconds()) / 60
            ));
            const pct = Math.max(0, Math.min(100, (minLeft / 120) * 100));
            const preview = (post.value ?? '')
              .replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
              .replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1')
              .slice(0, 90);
            return (
              <Box key={post.id} sx={{ px: 2, py: 1.25, position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
                      <Box sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                        @{author?.username ?? '???'}
                      </Box>
                      <Box sx={{ fontSize: '0.72rem', color: '#ff6b6b', fontFamily: '"VT323", monospace', letterSpacing: 0.5 }}>
                        {minLeft}m left
                      </Box>
                    </Box>
                    <Link
                      to={`/posts/${post.id}`}
                      style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
                    >
                      <Box sx={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preview}
                      </Box>
                    </Link>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        mt: 0.5,
                        height: 2,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: pct < 25 ? '#b51e1b' : pct < 50 ? '#ff9800' : '#4caf50',
                        },
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!userRecord || saving === post.id}
                    onClick={() => handleSave(post)}
                    startIcon={<HourglassFullIcon style={{ width: 14, height: 14 }} />}
                    sx={{
                      flexShrink: 0,
                      bgcolor: 'rgba(76,175,80,0.95)',
                      color: 'white',
                      textTransform: 'none',
                      fontFamily: '"VT323", monospace',
                      letterSpacing: 1,
                      fontSize: '0.85rem',
                      px: 1.25,
                      py: 0.4,
                      minWidth: 70,
                      '&:hover': { bgcolor: 'rgba(76,175,80,1)' },
                      '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' },
                    }}
                  >
                    {saving === post.id ? '…' : 'SAVE'}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}

export default LastCall;
